import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Link2,
  Loader2,
  Swords,
  X,
  UserMinus,
  UserPlus,
  UserCheck,
} from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useGetUserPresenceQuery,
  useAcceptFriendshipMutation,
  useRemoveFriendshipMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
} from '@/services/socialApi';
import {
  useCreateChallengeMutation,
  useGetMyLinkQuery,
} from '@/services/qduelsApi';
import { classifyQduelsError, isNotLinkedError } from '@/services/qduelsErrors';
import { cn } from '@/lib/cn';
import type { PresenceStatus } from '@/types/social';
import type { DuelFormat, DuelGameMode } from '@/types/qduels';

import { ChallengeOptionsSheet } from './ChallengeOptionsSheet';
import { PresenceBadge } from './PresenceBadge';
import { UserAvatar } from './UserAvatar';

type ItemMode = 'friend' | 'follow' | 'follower' | 'incoming' | 'outgoing';

interface FriendListItemProps {
  userId: string;
  /**
   * Synced profile display name (Wave 2). When absent we fall back to the
   * legacy `nickname`, then to a generic "QLASH player" label — we never
   * surface the raw Logto sub to users.
   */
  displayName?: string | null;
  /** Synced profile avatar URL (Wave 2). */
  avatarUrl?: string | null;
  /** Optional nickname; used only when no synced displayName is present. */
  nickname?: string;
  mode: ItemMode;
  /** Hide the presence badge when not relevant (e.g. pending request row). */
  hidePresence?: boolean;
  className?: string;
}

/**
 * Single row in any of the social lists (Friends / Following / Followers /
 * Requests). The footer actions adapt to `mode`:
 *
 *   friend    → Remove
 *   follow    → Unfollow
 *   follower  → Follow back (if iFollow=false) / nothing otherwise
 *   incoming  → Accept · Decline
 *   outgoing  → Cancel (delete the friendship row)
 *
 * Presence dot is fetched per-row via /presence/:userId. The query is cached
 * by RTK Query so opening multiple sub-tabs of the same user only hits the
 * API once.
 */
export function FriendListItem({
  userId,
  displayName,
  avatarUrl,
  nickname,
  mode,
  hidePresence,
  className,
}: FriendListItemProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const presenceQuery = useGetUserPresenceQuery(userId, { skip: hidePresence });
  const status: PresenceStatus = presenceQuery.data?.presence.status ?? 'offline';

  const [acceptFriendship, accepting] = useAcceptFriendshipMutation();
  const [removeFriendship, removing] = useRemoveFriendshipMutation();
  const [followUser, following] = useFollowUserMutation();
  const [unfollowUser, unfollowing] = useUnfollowUserMutation();

  const busy =
    accepting.isLoading ||
    removing.isLoading ||
    following.isLoading ||
    unfollowing.isLoading;

  const onAccept = () => {
    haptics.notification('success');
    acceptFriendship({ friendUserId: userId });
  };
  const onRemove = () => {
    haptics.impact('medium');
    removeFriendship({ friendUserId: userId });
  };
  const onFollow = () => {
    haptics.impact('light');
    followUser({ followedUserId: userId });
  };
  const onUnfollow = () => {
    haptics.impact('light');
    unfollowUser({ followedUserId: userId });
  };
  const openProfile = () => {
    haptics.selection();
    navigate(`/profile/${encodeURIComponent(userId)}`);
  };

  // QDuels 1v1 challenge (friends list only). `defenderLogtoId` is the friend's
  // canonical QLASH user id — the same value QSocials stores as `friendUserId`
  // (Logto sub), which this row receives as `userId`. Defaults: bo3 / REGULAR.
  //
  // Gate: a challenge fails with `challenger_not_linked` if the CURRENT user
  // hasn't linked a Clash Royale account. We probe qduels' link status up front
  // (`GET /v1/players/me`, 404 = not linked) so the Sfida button can route the
  // user to /duels/link instead of firing a request that's guaranteed to 4xx.
  // The query is RTK-Query-cached + deduped, so rendering N friend rows still
  // only hits the endpoint once. Skipped on non-friend rows (no Sfida there).
  const isFriendRow = mode === 'friend';
  const myLinkQuery = useGetMyLinkQuery(undefined, { skip: !isFriendRow });
  // Treat ONLY a 404 as "not linked". A transient auth/network/5xx error must
  // not mask the button into the link affordance — let the challenge attempt
  // surface the real error instead. While the probe is still loading we
  // optimistically allow the challenge; if the server then returns
  // `challenger_not_linked`, the catch below refetches the probe so the button
  // flips to the "link first" affordance.
  const challengerNotLinked = isNotLinkedError(myLinkQuery.error);

  const [createChallenge, creatingChallenge] = useCreateChallengeMutation();
  const [challengeSent, setChallengeSent] = useState(false);
  const [challengeMessage, setChallengeMessage] = useState<string | null>(null);
  const [challengeSheetOpen, setChallengeSheetOpen] = useState(false);
  const challengeBusy = creatingChallenge.isLoading;

  const goLinkCr = () => {
    haptics.impact('light');
    navigate('/duels/link');
  };

  // Tap on "Sfida": not-linked → link flow; otherwise open the options sheet so
  // the challenger picks the game mode + best-of before sending.
  const onChallengeTap = () => {
    if (challengeBusy || challengeSent) return;
    if (challengerNotLinked) {
      goLinkCr();
      return;
    }
    haptics.impact('light');
    setChallengeMessage(null);
    setChallengeSheetOpen(true);
  };

  // Fired by ChallengeOptionsSheet with the chosen mode + series.
  const submitChallenge = async ({
    format,
    gameMode,
  }: {
    format: DuelFormat;
    gameMode: DuelGameMode;
  }) => {
    if (challengeBusy || challengeSent) return;
    haptics.impact('medium');
    setChallengeMessage(null);
    try {
      await createChallenge({
        defenderLogtoId: userId,
        format,
        gameMode,
        createdVia: 'telegram',
      }).unwrap();
      haptics.notification('success');
      setChallengeSent(true);
      setChallengeSheetOpen(false);
      setChallengeMessage(
        t('social.challenge_sent', { defaultValue: 'Challenge sent!' }),
      );
    } catch (err) {
      haptics.notification('error');
      setChallengeSheetOpen(false);
      const kind = classifyQduelsError(err);
      // Server confirms we're not linked (the proactive probe may not have
      // resolved yet, or was a transient error). Refetch the link status so the
      // button flips to the "link first" affordance on the next render.
      if (kind === 'challenger_not_linked' && isFriendRow) {
        void myLinkQuery.refetch();
      }
      setChallengeMessage(
        kind === 'defender_not_linked'
          ? t('social.challenge_defender_not_linked', {
              defaultValue:
                "This friend hasn't linked their Clash Royale account yet.",
            })
          : kind === 'challenger_not_linked'
            ? t('social.challenge_challenger_not_linked', {
                defaultValue:
                  'Link your Clash Royale account first to send challenges.',
              })
            : kind === 'cannot_challenge_self'
              ? t('social.challenge_cannot_self', {
                  defaultValue: "You can't challenge yourself.",
                })
              : kind === 'auth'
                ? t('social.challenge_auth_error', {
                    defaultValue:
                      'Could not authenticate for challenges. Try signing in again.',
                  })
                : t('social.challenge_error', {
                    defaultValue: 'Could not send the challenge. Try again.',
                  }),
      );
    }
  };

  // Resolution order: synced profile name → legacy nickname → generic label.
  // We never display the raw Logto sub.
  const resolvedName =
    displayName ||
    nickname ||
    t('social.unknown_user', { defaultValue: 'QLASH player' });

  return (
    <li
      className={cn(
        'flex flex-col gap-2 rounded-xl bg-bg-secondary p-3',
        className,
      )}
    >
      <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={openProfile}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-70"
      >
        <div className="relative shrink-0">
          <UserAvatar
            userId={userId}
            label={resolvedName}
            avatarUrl={avatarUrl}
            size="md"
          />
          {!hidePresence && (
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-bg-secondary p-0.5">
              <PresenceBadge status={status} />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg">{resolvedName}</p>
          {!hidePresence && status !== 'offline' && (
            <p className="truncate text-[11px] text-fg-muted">
              {status === 'in_tournament'
                ? t('social.row_in_tournament', { defaultValue: 'In tournament' })
                : status === 'watching'
                  ? t('social.row_watching', { defaultValue: 'Watching live' })
                  : t('social.row_online', { defaultValue: 'Online' })}
            </p>
          )}
          {mode === 'outgoing' && (
            <p className="text-[11px] text-fg-muted">
              {t('social.request_pending', { defaultValue: 'Request pending' })}
            </p>
          )}
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-1.5">
        {mode === 'incoming' && (
          <>
            <RowButton
              variant="primary"
              onClick={onAccept}
              disabled={busy}
              aria-label={t('social.accept', { defaultValue: 'Accept' })}
            >
              <Check className="h-4 w-4" />
            </RowButton>
            <RowButton
              variant="secondary"
              onClick={onRemove}
              disabled={busy}
              aria-label={t('social.decline', { defaultValue: 'Decline' })}
            >
              <X className="h-4 w-4" />
            </RowButton>
          </>
        )}
        {mode === 'outgoing' && (
          <RowButton
            variant="secondary"
            onClick={onRemove}
            disabled={busy}
            aria-label={t('social.cancel_request', { defaultValue: 'Cancel' })}
          >
            <X className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'friend' && (
          <RowButton
            variant={challengerNotLinked ? 'secondary' : 'primary'}
            onClick={onChallengeTap}
            disabled={busy || challengeBusy || challengeSent}
            aria-label={
              challengerNotLinked
                ? t('social.challenge_link_cta', {
                    defaultValue: 'Link your Clash Royale account to challenge',
                  })
                : t('social.challenge', { defaultValue: 'Challenge' })
            }
          >
            {challengeBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : challengeSent ? (
              <Check className="h-4 w-4" />
            ) : challengerNotLinked ? (
              <Link2 className="h-4 w-4" />
            ) : (
              <Swords className="h-4 w-4" />
            )}
          </RowButton>
        )}
        {mode === 'friend' && (
          <RowButton
            variant="secondary"
            onClick={onRemove}
            disabled={busy}
            aria-label={t('social.remove_friend', { defaultValue: 'Remove' })}
          >
            <UserMinus className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'follow' && (
          <RowButton
            variant="secondary"
            onClick={onUnfollow}
            disabled={busy}
            aria-label={t('social.unfollow', { defaultValue: 'Unfollow' })}
          >
            <UserCheck className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'follower' && (
          <RowButton
            variant="primary"
            onClick={onFollow}
            disabled={busy}
            aria-label={t('social.follow_back', { defaultValue: 'Follow' })}
          >
            <UserPlus className="h-4 w-4" />
          </RowButton>
        )}
      </div>
      </div>

      {/* Challenge (QDuels) feedback line — success ("Sfida inviata!") or a
          mapped error. Only friend rows render the Challenge action, so this
          stays empty for every other mode. When the current user isn't linked
          yet, we show a tappable "link first" affordance instead of an error. */}
      {isFriendRow && challengerNotLinked && !challengeMessage && (
        <button
          type="button"
          onClick={goLinkCr}
          className="self-start text-[11px] text-fg-muted underline transition-opacity active:opacity-80"
        >
          {t('social.challenge_link_first', {
            defaultValue: 'Link your Clash Royale account first to challenge',
          })}
        </button>
      )}
      {challengeMessage && (
        <p
          className={cn(
            'text-[11px]',
            challengeSent ? 'text-success' : 'text-danger',
          )}
          role="status"
        >
          {challengeMessage}
        </p>
      )}

      {challengeSheetOpen && (
        <ChallengeOptionsSheet
          open
          opponentName={resolvedName}
          busy={challengeBusy}
          onSubmit={submitChallenge}
          onClose={() => setChallengeSheetOpen(false)}
        />
      )}
    </li>
  );
}

function RowButton({
  variant,
  children,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant: 'primary' | 'secondary';
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full transition-opacity active:opacity-80 disabled:opacity-50',
        variant === 'primary'
          ? 'bg-brand text-brand-fg'
          : 'bg-bg-tertiary text-fg-muted',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
